import { type MutableRefObject, useLayoutEffect, useState, useRef } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { JiraAutodevStatus } from '@atlassian/jira-relay/src/__generated__/uiGetAutodevJobsQuery.graphql';
import type { Screen } from './types';

export const fireAutodevErrorAnalytics = ({
	id,
	jobId,
	jobWorkflow,
	jobState,
	error,
}: {
	id: string;
	jobId?: string;
	jobWorkflow?: string;
	jobState?: string;
	error: Error;
}) =>
	fireErrorAnalytics({
		error,
		meta: {
			id,
			packageName: 'jiraAutodev',
			teamName: 'autodev',
		},
		attributes: {
			jobId,
			jobWorkflow,
			jobState,
		},
		sendToPrivacyUnsafeSplunk: true,
	});

export const getRepoName = (repoUrl: string) =>
	repoUrl.replace(
		/https:\/\/(bitbucket\.org|github\.com|integration\.bb-inf\.net|gitlab\.com)\//,
		'',
	);

/**
 * Hook to watch an element for it's content overflowing width.
 * @param ref element to watch for width overflow
 * @param callback optional callback, called with a boolean value indicating if the element currently has width overflow
 * @returns boolean value indicating whether the element's content is overflowing
 */
export const useIsOverflowWidth = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: MutableRefObject<any>,
	callback?: (isOverflowing: boolean) => void,
) => {
	const [isOverflow, setIsOverflow] = useState(false);
	const observer = useRef<ResizeObserver>();

	useLayoutEffect(() => {
		if (observer.current) {
			observer.current.disconnect();
		}
		const { current } = ref;

		const trigger = () => {
			const hasOverflow = current.scrollWidth > current.clientWidth;
			setIsOverflow(hasOverflow);
			if (callback) callback(hasOverflow);
		};

		if ('ResizeObserver' in window) {
			observer.current = new ResizeObserver(trigger);
		}
		if (current) {
			observer.current?.observe(current);
		}

		trigger();

		return () => {
			observer.current?.disconnect();
		};
	}, [callback, ref]);

	return isOverflow;
};

const planGeneratingPendingWorkflows: Set<string> = new Set([
	'SetupRepoWorkflow',
	'PullRepoWorkflow',
	'CodePlanningWorkflow',
	'DetectPrimaryLanguageWorkflow',
]);

const codeGeneratingWorkflows: Set<string> = new Set([
	'WaitForUserInputWorkflow',
	'PipeInputWorkflow',
	'CodeGenerationWorkflow',
	'CodeReviewWorkflow',
	'CodeCleanupWorkflow',
	'CodeBuildWorkflow',
	'CodeLintWorkflow',
	'PushRepoWorkflow',
	'FunctionalErrorFixerWorkflow',
	'GetLatestDiffWorkflow',
	'GetChangedFilesWorkflow',
	'DoWhileWorkflow',
	'CodeTestWorkflow',
	'ParallelWorkflow',
]);

const codeGeneratingCompleteWorkflows: Set<string> = new Set([
	'PushRepoWorkflow',
	'GetLatestDiffWorkflow',
]);

const pullRequestGeneratingWorkflows: Set<string> = new Set(['PullRequestWorkflow']);
export const determineJobScreen = (
	workflow: string | null | undefined,
	status: JiraAutodevStatus | null | undefined,
): Screen => {
	switch (status) {
		case 'IN_PROGRESS':
			if (planGeneratingPendingWorkflows.has(workflow ?? '')) return 'plan_generating';
			if (codeGeneratingWorkflows.has(workflow ?? '')) return 'coding_task';
			if (pullRequestGeneratingWorkflows.has(workflow ?? '')) return 'pull_request_creating';
			return 'unknown';
		case 'PENDING':
			if (codeGeneratingWorkflows.has(workflow ?? '')) return 'code_generation_success';
			if (planGeneratingPendingWorkflows.has(workflow ?? '')) return 'review_plan';
			if (workflow === 'None') return 'plan_generating';
			return 'unknown';
		case 'FAILED':
			if (planGeneratingPendingWorkflows.has(workflow ?? '')) return 'plan_generation_fail';
			if (codeGeneratingWorkflows.has(workflow ?? '')) return 'code_generation_fail';
			if (pullRequestGeneratingWorkflows.has(workflow ?? '')) return 'pull_request_failure';

			return 'unknown';
		case 'COMPLETED':
			if (workflow === 'PullRequestWorkflow') return 'view_pull_request';
			if (codeGeneratingCompleteWorkflows.has(workflow ?? '')) return 'code_generation_success';
			if (workflow === 'CodePlanningWorkflow') return 'review_plan';
			return 'unknown';
		case 'CANCELLED':
			return 'cancelled';
		default:
			return 'choose_repository';
	}
};

export const sanitiseFilePath = (value: string) =>
	value
		// Remove leading whitespace.
		.trimStart()
		// Replace inner whitespace with dashes.
		// We shouldn't really need to do this, but the backend isn't escaping things properly yet :|
		.replace(/\s+/g, '-')
		// Strip all special characters except for obviously-valid ones (forward-slashes, full-stops, and underscores).
		.replace(/[^a-zA-Z0-9\\/\\_\-\\.]/g, '');

export const assertUnreachable = (_: never): never => {
	throw new Error('Unreachable code reached');
};
